import { Modal } from "antd";
import { VerticalSpacing } from "src/designsystem/VerticalSpacing/VerticalSpacing";

import { BsPeople, BsPerson } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { PostShiftOption } from "./PostShiftOption";
import { Caption, ModalContent } from "./PostShiftOptionsModal.styles";
import { logEvent } from "@src/appV2/lib/analytics";
import { FacilityOnboardingStep } from "src/api/facility";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { useEffect } from "react";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { useRestrictedSignedProfile } from "src/hooks/useRestrictedSignedProfile";

const INTERMEDIATE_SHIFT_POSTING_OPTIONS = {
  CLOSE: "close",
  POST_BLOCK_SHIFTS: "post block-shifts",
  POST_PER_DIEM_SHIFT: "post per-diem shift",
} as const;

type IntermediateShiftPostingActionKeys = keyof typeof INTERMEDIATE_SHIFT_POSTING_OPTIONS;
type IntermediateShiftPostingActionValues =
  (typeof INTERMEDIATE_SHIFT_POSTING_OPTIONS)[IntermediateShiftPostingActionKeys];

export interface PostShiftOptionsModalProps {
  visible: boolean;
  onCancel: () => void;
  postShiftOptionSteps: Array<"block-shifts" | "per-diem">;
  workplaceId: string;
  workplaceUserId?: string;
  onboardingState: FacilityOnboardingStep;
  isAdmin: boolean;
}

export function PostShiftOptionsModal({
  visible,
  onCancel,
  postShiftOptionSteps,
  workplaceId,
  workplaceUserId,
  onboardingState,
  isAdmin,
}: PostShiftOptionsModalProps) {
  const history = useHistory();
  const { isSignedProfile, openContractModal } = useRestrictedSignedProfile();

  const logIntermediateShiftPostingModalEvents = (action: IntermediateShiftPostingActionValues) => {
    if (isAdmin) {
      return;
    }

    const commonAttributes = {
      workplaceId,
      workplaceUserId,
      onboardingState,
      postShiftOptionSteps,
    };

    logEvent(HCF_USER_EVENTS.INTERMEDIATE_SHIFT_POSTING_OPTIONS, {
      ...commonAttributes,
      action,
    });
  };

  useEffect(() => {
    function logPageView() {
      logEvent(HCF_USER_EVENTS.INTERMEDIATE_SHIFT_POSTING_OPTIONS_VIEWED, {
        workplaceId,
        workplaceUserId,
        onboardingState,
        postShiftOptionSteps,
      });
    }
    if (!isAdmin) {
      logPageView();
    }
  }, [isAdmin, onboardingState, postShiftOptionSteps, workplaceId, workplaceUserId]);

  const handleCancel = () => {
    logIntermediateShiftPostingModalEvents(INTERMEDIATE_SHIFT_POSTING_OPTIONS.CLOSE);
    onCancel();
  };

  const renderOptions = () => {
    const handleClick = (type: "block-shifts" | "per-diem") => {
      if (type === "block-shifts") {
        if (isSignedProfile) {
          logIntermediateShiftPostingModalEvents(
            INTERMEDIATE_SHIFT_POSTING_OPTIONS.POST_BLOCK_SHIFTS
          );
          history.push(getLocation("workplaceBlockShifts"), {
            previousLocation: "IntermediateShiftPostingModal",
          });
        } else {
          openContractModal({
            triggeredBy: "offer a block of shifts",
            triggeredFrom: "Get Started",
          });
          onCancel();
        }
      } else {
        logIntermediateShiftPostingModalEvents(
          INTERMEDIATE_SHIFT_POSTING_OPTIONS.POST_PER_DIEM_SHIFT
        );
        history.push(getLocation("facilityDashboard"));
      }
    };
    return postShiftOptionSteps.map((option) => {
      switch (option) {
        case "block-shifts":
          return (
            <PostShiftOption
              type="block-shifts"
              key={option}
              heading="A block of shifts"
              description="Connect with qualified professionals seeking recurring shifts."
              icon={<BsPerson />}
              onClick={() => handleClick(option)}
              isAdmin={isAdmin}
            />
          );
        case "per-diem":
          return (
            <PostShiftOption
              type="per-diem"
              key={option}
              heading="Individual shifts"
              description="Connect with qualified professionals seeking individual shifts."
              icon={<BsPeople />}
              onClick={() => handleClick(option)}
              isAdmin={isAdmin}
            />
          );
        default:
          return null;
      }
    });
  };
  return (
    <Modal visible={visible} width={780} maskClosable={false} onCancel={handleCancel} footer={null}>
      <ModalContent>
        <VerticalSpacing size="xxl" />
        <Caption>What types of shifts do you want to post first?</Caption>
        <VerticalSpacing size="lg" />
        {renderOptions()}
        <VerticalSpacing size="xxl" />
      </ModalContent>
    </Modal>
  );
}
